import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import defaultProfile from "../../../assets-user/agents/default_avatar.png";
import api from "../../../configs/api";
import { useOutsideClick } from "../../../hooks/useOutsideClick";
import {
  confirmAction,
  toastError,
  toastSuccess,
} from "../../../utils/confirmAction";
import { formatDate } from "../../../utils/helpers";
import ApprovalCard from "./ApprovalCard";
import ApprovalCardSkeleton from "./ApprovalCardSkeleton";

const AgentDetailsModal = ({ agent, onClose }) => {
  const modalRef = React.useRef(null);

  useOutsideClick(modalRef, onClose, !!agent);

  if (!agent) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[9999] bg-black/40 flex items-center justify-center p-4">
      <div
        ref={modalRef}
        className="bg-white w-full max-w-2xl rounded-xl shadow-xl p-6 relative"
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute right-4 top-4 text-gray-500 hover:text-gray-800"
        >
          ✕
        </button>

        {/* Profile Section */}
        <div className="flex gap-4 mb-5">
          <img
            src={agent.profileImage || defaultProfile}
            className="w-20 h-20 rounded-full object-cover border"
            alt=""
          />
          <div>
            <h2 className="text-lg font-semibold text-text-primary">
              {agent.fullName}
            </h2>
            <p className="text-sm text-text-muted">{agent.email}</p>
            <p className="text-sm text-text-muted">{agent.phone}</p>
            <p className="text-sm mt-1 text-status-info">
              City: {agent.city || "—"}
            </p>
          </div>
        </div>

        {/* Details Grid */}
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="font-medium text-text-secondary">RERA ID</p>
            <p>{agent.reraId || "—"}</p>
          </div>

          <div>
            <p className="font-medium text-text-secondary">Operating Since</p>
            <p>{agent.operatingSince || "—"}</p>
          </div>

          <div>
            <p className="font-medium text-text-secondary">Operating Areas</p>
            <p>{agent.operatingAreas?.join(", ") || "—"}</p>
          </div>

          <div>
            <p className="font-medium text-text-secondary">Deal Types</p>
            <p>{agent.dealTypes?.join(", ") || "—"}</p>
          </div>

          <div>
            <p className="font-medium text-text-secondary">
              Properties for Sale
            </p>
            <p>{agent.propertiesForSale}</p>
          </div>

          <div>
            <p className="font-medium text-text-secondary">Team Members</p>
            <p>{agent.teamMembers}</p>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

const AgentBuilderApprovalList = ({ list: data, loading }) => {
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [mappedList, setMappedList] = useState([]);

  useEffect(() => {
    if (!data || data.length === 0) {
      setMappedList([]);
      setFiltered([]);
      return;
    }

    const list = data.map((ag) => ({
      id: ag._id,
      fullName: ag?.userId?.fullName || "",
      email: ag?.userId?.email || "",
      phone: ag?.userId?.phone || "",
      city: ag?.userId?.preferredCity || "",
      profileImage: ag?.userId?.profileImage || "",
      reraId: ag.reraId,
      operatingAreas: ag.operatingAreas || [],
      dealTypes: ag.dealTypes || [],
      teamMembers: ag.teamMembers,
      propertiesForSale: ag.propertiesForSale,
      operatingSince: ag.operatingSince,
      createdAtFormatted: formatDate(ag.createdAt),
    }));

    setMappedList(list);
    setFiltered(list);
  }, [data]);

  useEffect(() => {
    const s = search.toLowerCase();
    setFiltered(mappedList.filter((i) => i.fullName.toLowerCase().includes(s)));
  }, [search, mappedList]);

  const handleApprove = async (agent) => {
    setSelected(null);
    const confirm = await confirmAction(
      "Approve Agent?",
      `This will approve ${agent?.fullName} as an Agent/Builder.`,
      "Approve"
    );

    if (!confirm.isConfirmed) return;

    try {
      await api.post(`/approvals/agent-builders/approve/${agent.id}`);

      setFiltered((prev) => prev.filter((x) => x.id !== agent.id));

      toastSuccess("Agent approved successfully!");
      setSelected(null);
    } catch (err) {
      console.error(err);
      toastError("Failed to approve agent");
    }
  };

  const handleReject = async (agent) => {
    setSelected(null);
    const confirm = await confirmAction(
      "Reject Request?",
      `This will reject ${agent.fullName}'s request.`,
      "Reject"
    );

    if (!confirm.isConfirmed) return;

    try {
      await api.post(`/approvals/agent-builders/reject/${agent.id}`);

      setFiltered((prev) => prev.filter((x) => x.id !== agent.id));

      toastSuccess("Agent rejected successfully!");
      setSelected(null);
    } catch (err) {
      console.error(err);
      toastError("Failed to reject agent");
    }
  };

  return (
    <div className="p-4">
      {/* Search */}
      <input
        type="text"
        placeholder="Search Agent/Builder"
        className="border border-border px-4 py-2 rounded-lg mb-4 w-full focus:outline-primary"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {/* Loading Skeletons */}
      {loading && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {Array.from({ length: 6 }).map((_, i) => (
            <ApprovalCardSkeleton key={i} />
          ))}
        </div>
      )}

      {/* Content */}
      {!loading && filtered.length > 0 && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((item) => (
            <ApprovalCard
              key={item.id}
              item={item}
              onSelect={() => setSelected(item)}
              onApprove={handleApprove}
              onReject={handleReject}
            />
          ))}
        </div>
      )}

      {/* Empty State */}
      {!loading && filtered.length === 0 && (
        <p className="text-center text-text-muted mt-10">
          No pending approvals found.
        </p>
      )}

      {/* Modal */}
      <AgentDetailsModal agent={selected} onClose={() => setSelected(null)} />
    </div>
  );
};

export default AgentBuilderApprovalList;
